/**
 * Map raw caught errors to fiduciary-register, user-facing strings for the
 * web app — the desktop twin of `mobile/src/lib/error-messages.ts`.
 *
 * 2026-05-20 (Round-2 REG-3 / F02-C5-01 closure): ~14 web call sites were
 * interpolating a raw `err.message` straight into a toast or inline banner
 * (e.g. ``Save failed: ${err.message}``). The raw message is often a library
 * or infra string (Cosmos partition-key text, a Supabase RLS error, a fetch
 * failure) and leaks implementation detail to a settlor/trustee/firm-admin.
 *
 * Mirrors mobile's `mapError` (status-code first, then a couple of network
 * prefixes, then a context-specific catch-all). The raw `err.message` is
 * intentionally NEVER returned — call sites should log the raw error to
 * telemetry separately for triage.
 *
 * Auth (Supabase) errors keep their dedicated mapper, `mapSupabaseAuthError`
 * in `auth-errors.ts`; this helper is for the API/mutation surfaces.
 */

export type ErrorContext =
  | 'generic'
  | 'save'
  | 'publish'
  | 'load'
  | 'invite'
  | 'connect'
  | 'approve'
  | 'reject'
  | 'submit-request'
  | 'draft'
  | 'circulate'
  | 'onboard'
  | 'grant'
  | 'revoke';

interface MappedErrorInput {
  status?: number;
  statusCode?: number;
  errorCode?: string;
  code?: string;
  message?: string;
  isStepUpRequired?: boolean;
  /** The parsed JSON error envelope, which `ApiError` carries verbatim. NOTE
   *  that `message` above is the CLIENT's own string ("API POST /v1/... failed:
   *  502"), not the backend's sentence: the backend one lives at `body.message`
   *  and is the only one worth showing a person. */
  body?: unknown;
}

/** A string field off the backend's error envelope, or undefined. */
function envelopeField(e: MappedErrorInput, key: string): string | undefined {
  if (!e.body || typeof e.body !== 'object') return undefined;
  const value = (e.body as Record<string, unknown>)[key];
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}

/** What the server said about whether a step-up challenge can succeed at all.
 *  Mirrors `ApiError.stepUpPossible`, read off the plain envelope so this
 *  mapper keeps working on a `{ status, code, body }` literal. `undefined` from
 *  an older backend, which is read as "assume it can" so a version skew fails
 *  the way it always did rather than a new way. */
function stepUpPossibleOf(e: MappedErrorInput): boolean | undefined {
  if (!e.body || typeof e.body !== 'object') return undefined;
  const value = (e.body as Record<string, unknown>)['stepUpPossible'];
  return typeof value === 'boolean' ? value : undefined;
}

/**
 * IS THIS 5xx BODY A SENTENCE WRITTEN FOR A PERSON, OR IS IT INFRASTRUCTURE?
 *
 * Our handlers write plain refusals into `body.message` on a 5xx too: the
 * billing 502 says, in words, that the provider did not answer and nothing was
 * charged. That sentence never reached a screen, because this file had no
 * branch that read a body on a 5xx, so every reader got "The server had a
 * problem. Please try again shortly." True, and useless.
 *
 * But a 5xx body is the LEAST trustworthy place in the product to take copy
 * from: it is where an unhandled error's own text leaks. So it has to look
 * like a sentence somebody wrote before it is shown, and anything that smells
 * of infrastructure falls back to the generic line:
 *
 *   · not a non-empty string, or 200 characters or longer;
 *   · carries a URL or a bare host path;
 *   · carries an identifier this product mints (a uuid, a Paystack plan or
 *     subscription code, a secret key prefix);
 *   · carries stack or module text.
 *
 * Erring toward the generic line is the right direction for a mistake: the
 * cost is a vaguer sentence, not a leak.
 */
function serverSentence(e: MappedErrorInput): string | undefined {
  const raw = envelopeField(e, 'message');
  if (!raw || raw.length >= 200) return undefined;
  if (/\s/.test(raw) === false) return undefined; // a single token is a code, not a sentence
  if (/https?:\/\/|:\/\/|www\.|[\w.-]+\.(io|com|net|local)\//i.test(raw)) return undefined;
  if (/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(raw)) return undefined;
  if (/\b(PLN|SUB)_[\w-]+|\bsk_[\w-]+/i.test(raw)) return undefined;
  if (/[\r\n]|\bat\s+\S+\.|node_modules|\.(ts|js|mjs|cjs):\d|\b\w*Error:/.test(raw)) return undefined;
  return raw;
}

/** Paystack states where the subscription is alive and the firm owes money.
 *  Mirrors `ARREARS_SUBSCRIPTION_STATUSES` in
 *  `backend/src/handlers/admin-billing-subscription-update.ts`. */
const ARREARS_PROVIDER_STATUSES: ReadonlySet<string> = new Set(['attention', 'non-renewing']);

const CONTEXT_FALLBACK: Record<ErrorContext, string> = {
  generic: 'Something went wrong. Please try again, or contact your administrator if it persists.',
  save: 'We could not save your changes. Please try again.',
  publish: 'We could not publish your changes. Please try again.',
  load: 'We could not load this just now. Refresh and try again.',
  invite: 'We could not send the invitation. Please try again.',
  connect: 'We could not connect that integration. Please try again.',
  approve: 'We could not approve that request. Please try again.',
  reject: 'We could not decline that request. Please try again.',
  'submit-request': 'We could not submit your request. Please try again.',
  draft: 'We could not save the draft. Please try again.',
  circulate: 'We could not circulate the resolution. Please try again.',
  onboard: 'We could not finish setting up your trust. Please try again.',
  grant: 'We could not create the auditor grant. Please try again.',
  revoke: 'We could not revoke that access. Please try again.',
};

/**
 * WHICH CONTEXTS MAY ASSERT "You are signed out."
 *
 * A DEFAULT SHOULD BE THE SAFEST CLAIM THIS FUNCTION CAN MAKE, NOT THE
 * STRONGEST ONE. "You are signed out" is the most alarming sentence in this
 * file, and until 2026-08-27 it was what a caller got for not thinking about
 * the argument at all: three panel data-fetch renders passed no context, so a
 * single 401 in one card printed that sentence beside live data on a screen the
 * user was plainly still signed into.
 *
 * A 401 on ONE call is never a verdict on the session. It becomes a reasonable
 * reading only when the user has just asked for something and been refused,
 * which is what an action context records. So the strong claim now requires a
 * caller to have named such an action; 'generic' (the default) and 'load' get a
 * sentence that says what a single 401 actually proves.
 *
 * This is an ALLOWLIST on purpose. A context added tomorrow and forgotten here
 * falls to the safe copy, which is the direction a mistake should fall.
 */
const SESSION_VERDICT_CONTEXTS: ReadonlySet<ErrorContext> = new Set<ErrorContext>([
  'save',
  'publish',
  'invite',
  'connect',
  'approve',
  'reject',
  'submit-request',
  'draft',
  'circulate',
  'onboard',
  'grant',
  'revoke',
]);

/**
 * Take a caught error (or a plain `{ status, code, message }` shape) and
 * return a user-facing string. The raw `e.message` is intentionally NOT
 * included in the output.
 */
export function mapError(err: unknown, context: ErrorContext = 'generic'): string {
  const e = (err ?? {}) as MappedErrorInput;
  const status = typeof e.status === 'number' ? e.status : typeof e.statusCode === 'number' ? e.statusCode : undefined;
  const errorCode =
    typeof e.errorCode === 'string' ? e.errorCode : typeof e.code === 'string' ? e.code : undefined;
  const msg = (typeof e.message === 'string' ? e.message : '').toLowerCase();

  // --- Step-up (highest signal) ---------------------------------------------
  //
  // TWO DIFFERENT REFUSALS WEAR THIS ONE CODE, and until round 11 they wore
  // one sentence. `requireStepUp` answers `stepUpPossible: false` when NO
  // challenge can succeed in the caller's posture: a firm operator working
  // inside a client's account cannot produce the account holder's
  // authenticator, however many times they re-authenticate. Telling them to
  // "sign in again, then retry" costs them the session and then refuses again,
  // which is the NEW-STEPUP-COPY finding (and, through the beneficial-ownership
  // dialog's blanket 403 arm, NEW-BO-READONLY-COPY as well).
  //
  // The server writes the true sentence into the same body, so render that and
  // fall back to one that at least does not prescribe the impossible.
  if (e.isStepUpRequired === true || errorCode === 'STEP_UP_REQUIRED') {
    if (stepUpPossibleOf(e) === false) {
      return (
        envelopeField(e, 'message') ??
        'This needs a security confirmation from the account holder, so it cannot be done from where you are. Leave the client and do it from your own sign-in, or ask the account holder to do it.'
      );
    }
    return 'This action needs you to confirm it with your authenticator. Sign in again, then retry.';
  }

  // --- Payment required (NEW-ING-402-TOAST, round 11) -----------------------
  //
  // A 402 is never "we could not save your changes, please try again": nothing
  // failed and retrying cannot work. The ingestion billing gate
  // (`backend/src/lib/billing/ingestion-entitlement.ts`) writes a sentence that
  // names the payer, the client and the one action that clears it, and the
  // browser was throwing it away for the 'save' catch-all. A user told to try
  // again tries forever and never hears the word billing.
  //
  // This reads `body.message`, the envelope our own handler wrote, never
  // `e.message` (the client's `API POST … failed: 402` infra string), so the
  // anti-leak rule at the top of this file still holds.
  if (status === 402) {
    return (
      envelopeField(e, 'message') ??
      'Reading documents for this workspace needs an active Elreign subscription. Check Billing, or ask the firm that looks after you to bring theirs up to date.'
    );
  }

  // --- Tenant suspended (COMBO-003) -----------------------------------------
  // A suspended firm's users get a 401 with code `tenant_suspended` from the
  // JWT gate. Without this branch the generic 401 below tells them "you are
  // signed out", which is misleading — their session is fine; their firm's
  // access was administratively suspended. Give them the real reason so they
  // contact their administrator instead of fruitlessly re-authenticating.
  if (errorCode === 'tenant_suspended') {
    return 'Your organisation’s access is currently suspended. Please contact your administrator.';
  }

  // --- Firm-staff personal-trust guard (owner rule, 2026-06-19) -------------
  // A firm-staff account cannot create/own a PERSONAL trust under the firm
  // login (the backend returns 403 FIRM_STAFF_PERSONAL_TRUST_FORBIDDEN). The
  // generic 403 below ("you do not have permission") would be misleading — this
  // is not a missing-permission problem, it's a "use a separate personal
  // account" rule. Surface the honest, actionable guidance instead.
  if (errorCode === 'FIRM_STAFF_PERSONAL_TRUST_FORBIDDEN') {
    return (
      'Your firm account is for managing client trusts, so it cannot hold a ' +
      'personal trust of your own. To open your own trust, sign up with a ' +
      'separate personal email (billed to you individually). It can still be ' +
      'administered under the firm.'
    );
  }

  // --- Document-first setup confirm (non-trust entity, 2026-06-23) ----------
  // A trustId-less confirm-entity request for a COMPANY / ASSET / generic card
  // used to force `createTrustIfMissing` for every entity, so the backend 422'd
  // with TRUST_REQUIRED and the generic 'save' fallback below ("We could not
  // save your changes.") fired — alarming for what is a handled outcome. The
  // frontend now only sets that flag for trusts, so this code should not occur
  // on a non-trust card. If it ever does (an unexpected backend state), surface a
  // calm, specific line instead of the blanket failure copy.
  if (errorCode === 'TRUST_REQUIRED') {
    // BUG-9: a trust whose documents carry no readable name/IT (and the reviewer
    // typed none) — name the exact remedy so the user knows to fill the fields on
    // the card, not the old cryptic "needs a trust to attach to".
    return (
      'We could not read this trust’s name or registration (IT) number from the ' +
      'documents. Add the trust name and/or its IT/registration number on the ' +
      'card above, then save again.'
    );
  }

  // --- FICA account-holder ID name-mismatch (2026-06-25) --------------------
  // The b2c first gate denies an identity document uploaded for a DIFFERENT
  // person than the account holder (422 FICA_ID_NAME_MISMATCH). Prefer the
  // backend's specific message — it names the name read OFF THE ID, which is the
  // user's OWN uploaded name (safe to show, not an infra string) — and fall back
  // to a calm, self-contained instruction. This is the ONLY branch that surfaces
  // a backend message verbatim, and only for this one well-known code, so the
  // anti-leak guarantee holds for every other path.
  if (errorCode === 'FICA_ID_NAME_MISMATCH') {
    const backendMsg = typeof e.message === 'string' ? e.message.trim() : '';
    if (backendMsg.length > 0) return backendMsg;
    return (
      'That identity document appears to be for a different name. Please upload ' +
      'your own identity document, the account holder’s.'
    );
  }

  // The caller's own tenant claim resolves to no workspace in THIS environment —
  // not a permission problem. Live case 2026-07-30: dev and prod share one
  // Supabase auth project, so a teammate who signed up on dev carries a dev
  // tenant id into prod; the setup surface then refused every call and this
  // mapper said "You do not have permission to do that." A real walkthrough
  // session was lost to that wording, so name the actual cause and the way out.
  if (errorCode === 'TENANT_NOT_PROVISIONED') {
    return (
      'This account has no workspace here yet. If you first signed up on a test ' +
      'environment, sign out and create a fresh account on this one.'
    );
  }

  // The SAME lesson, a second time. NO_TENANT_ASSIGNMENT had no entry here at
  // all, so it fell through to the generic "You do not have permission to do
  // that." — the identical sentence that cost the walkthrough session above.
  //
  // 2026-08-06: it cost another one. Beno signed up on 2026-05-19, never reached
  // the onboarding page that mints a tenant, and every call he made for the next
  // three months answered "You do not have permission to do that." He was not
  // being refused: his setup had never finished. 14 of 88 accounts are in that
  // state.
  //
  // "No permission" and "we never finished setting you up" are different facts
  // and must read differently. Do not collapse this back into the generic branch.
  if (errorCode === 'NO_TENANT_ASSIGNMENT') {
    return (
      'Your account is signed in but not yet linked to a workspace, so there is ' +
      'nothing here to show you. This is our setup step, not a permission problem. ' +
      'If someone invited you, ask them to resend the invitation; otherwise ' +
      'finish creating your account.'
    );
  }

  // --- Review-screen corrections refused (2026-08-23) -----------------------
  // The setup commit used to accept-and-drop field edits it had no writer for
  // (five review-screen fields persisted nowhere behind a 200). The server now
  // refuses the whole commit with 400 INVALID_OVERLAY instead — an edit that
  // will not be written must fail loudly. Without this branch that honest
  // refusal reads as the blanket "We could not save your changes", which hides
  // the two real causes: a typed value the server could not read (e.g. an
  // amount), or a review left open long enough that a card it references was
  // recomputed. Name both and the way out.
  if (errorCode === 'INVALID_OVERLAY') {
    return (
      'We could not apply one or more of your corrections. Check any values ' +
      'you typed (amounts and dates), refresh the review screen if it has ' +
      'been open a while, then save again.'
    );
  }

  // --- Historical resolution already on the register (Beno 2026-07-30) ------
  // `POST /v1/trusts/{id}/resolutions/import` 409s ALREADY_IMPORTED when the
  // source document already has a register row (the double-mint guard). The
  // generic 409 below — "Someone else updated this just now" — would send a
  // user who simply double-clicked off to refresh and try again, i.e. to
  // attempt the very thing that just correctly failed. Say what happened.
  if (errorCode === 'ALREADY_IMPORTED') {
    return 'That document is already on this trust’s resolutions register.';
  }

  // --- Inviting an address that already has an account (2026-08-23) ----------
  // Both of these used to fall through to a status-code branch and come out as
  // "Someone else updated this just now. Refresh and try again." — which sent
  // the 2026-08-22 walkthrough round the same loop three times, because
  // refreshing changes nothing about either condition.
  //
  // ACCOUNT_IN_ANOTHER_WORKSPACE is a REFUSAL, not a race: the address has a
  // live account signed in to a different firm, and attaching it here would take
  // it out of theirs. Name the actual obstacle and the two real ways past it.
  if (errorCode === 'ACCOUNT_IN_ANOTHER_WORKSPACE') {
    return (
      'That email already has an Elreign account with another firm, and moving ' +
      'it here would remove it from theirs, so nothing was changed and no ' +
      'email was sent. Invite them on a different address, or contact support ' +
      'to move the account across.'
    );
  }
  // The person exists, their access is saved, and the notification failed to
  // send. That is a delivery problem on a saved record — quite different from
  // "your change was rejected", and the difference decides whether the admin
  // re-invites (wrong) or re-sends (right).
  if (errorCode === 'EXISTING_ACCOUNT_EMAIL_FAILED') {
    return (
      'They already have an Elreign account and their access here is saved, but ' +
      'the sign-in notification did not send. Use “Re-send” on their row in a ' +
      'few minutes. Do not invite them again.'
    );
  }

  // --- New conflict codes, 21 Sep audit wave (T7) ----------------------------
  // Each of these used to fall through to the generic 409 copy below ("Someone
  // else updated this just now. Refresh and try again.") or did not exist at
  // all. None of them is the ordinary optimistic-concurrency race that
  // sentence describes, so each gets its own honest line.

  // The signature IS recorded (its own unique row is already durable); only
  // the running count on the resolution failed to update after three CAS
  // attempts. Saying "signing failed" here would be false and would invite a
  // re-sign that double-counts. Say what actually happened and how to see it.
  if (errorCode === 'SIGNATURE_COUNT_CONFLICT') {
    return 'Your signature was recorded, but the count could not be updated. Reload to see the current count.';
  }
  // Another approver decided this access request first. A refresh-and-retry
  // would just refuse again, so name the real state and point at it.
  if (errorCode === 'ACCESS_REQUEST_DECIDED') {
    return 'Someone else already decided this request. Reload to see the decision.';
  }
  // T7 fix round 2: the pre-existing status gate in `trust-claim.ts`
  // (`canDecideRequest`) — the ordinary "somebody already approved/declined
  // this" case, distinct from the true CAS-race code above (a grader walk of
  // approve-then-reject on a real backend hit this one, not that one). Its
  // backend `message` is dynamic but safe (`This request was already
  // ${row.status}.` — a decision-state word, never an infra string), so this
  // reads `body.message` first, same as the billing codes below, never the
  // client's own synthetic `.message`.
  if (errorCode === 'REQUEST_ALREADY_DECIDED') {
    return envelopeField(e, 'message') ?? 'This request was already decided. Reload to see the decision.';
  }
  // The resolution body or status changed between the review screen loading
  // and the signature landing. Reloading picks up the current text; signing
  // on stale text is the one thing that would fail again.
  if (errorCode === 'RESOLUTION_CHANGED') {
    return 'This resolution changed while you were signing. Reload and sign again.';
  }
  // The meeting minutes changed under a concurrent edit (agenda patch or
  // finalise). This is a genuine race, not "the meeting was finalised" — do
  // not conflate the two.
  if (errorCode === 'MEETING_CAS_RACE') {
    return 'The minutes changed while you were editing. Reload and try again.';
  }
  // A rare read-path failure: the row that was read back carries no revision
  // marker to write against, so the server refuses rather than risk an
  // unguarded write. Not actionable beyond retrying.
  if (errorCode === 'ACCESS_REQUEST_REVISION_UNKNOWN' || errorCode === 'TAX_CERTIFICATE_REVISION_UNKNOWN') {
    return 'We could not read the latest version of this record. Reload and try again.';
  }

  // --- The last five, M4 (final round of the same 21 Sep wave). Each is a 409
  // a handler already returns and this map did not know, so all five rendered
  // the generic "Somebody else updated this just now" over four unrelated
  // situations: a resolution edit, a director claim, the practice settings, and
  // a workspace invitation. Two of them are not even a concurrent edit.
  //
  // `*_REVISION_UNKNOWN` is the read-path case: the row came back with no
  // revision marker to write against and the server refused rather than write
  // blind. Telling somebody "another person changed this" would be a guess
  // about a person who may not exist.
  if (errorCode === 'RESOLUTION_REVISION_UNKNOWN') {
    return 'We could not read the latest version of this resolution. Reload and try again.';
  }
  if (errorCode === 'TENANT_REVISION_UNKNOWN') {
    return 'We could not read the latest version of your practice settings. Reload and try again.';
  }
  if (errorCode === 'INVITATION_REVISION_UNKNOWN') {
    return 'We could not read the latest version of this invitation. Reload and try again.';
  }
  // The two genuine races. `TENANT_CHANGED` covers both the practice-settings
  // save and the director claim, which write to the same document.
  if (errorCode === 'TENANT_CHANGED') {
    return 'Your practice settings changed while this was being saved. Reload and apply your change again.';
  }
  if (errorCode === 'INVITATION_CHANGED') {
    return 'This invitation changed while you were responding to it. Reload your workspaces to see where you stand.';
  }

  // --- Six more conflict codes, T7 fix round 1 (T6's CAS-write pass, same 21
  // Sep audit wave). Each backend handler already carries a human `message` in
  // the response body, but this file's own rule is that `err.message` is never
  // echoed except for the one declared FICA_ID_NAME_MISMATCH branch above, so
  // each of these gets its own line here too, in the same "reload" style.
  if (errorCode === 'BENEFICIAL_OWNER_CHANGED') {
    return 'Somebody else changed this beneficial owner while you were editing it. Reload and apply your change again.';
  }
  if (errorCode === 'BENEFICIARY_CHANGED') {
    return 'Somebody else changed this beneficiary while you were editing it. Reload and apply your change again.';
  }
  if (errorCode === 'LODGEMENT_ATTEMPT_CHANGED') {
    return 'Somebody else changed this lodgement while you were completing it. Reload to see where it stands.';
  }
  if (errorCode === 'HOLDING_CHANGED') {
    return 'Somebody else changed this holding while you were editing it. Reload and apply your change again.';
  }
  if (errorCode === 'LOA_CHANGED') {
    return 'The letter of authority on file changed while this one was being recorded. Reload the trust and record it again.';
  }
  if (errorCode === 'LOW_CHANGED') {
    return 'The letter of wishes on file changed while this version was being recorded. Reload the trust and record it again.';
  }

  // --- Billing plan change, 21 Sep audit wave (T3 fix round 4) --------------
  //
  // /admin/billing used to branch on a hardcoded list of three codes and send
  // everything else to "We couldn't apply the change. Please try again in a
  // moment." Each of the three below is a refusal with its own next move, and
  // for SUBSCRIPTION_SWAP_UNRECORDED "try again" is the one move that makes it
  // worse: the change DID land at Paystack, and the idempotency middleware
  // drops 5xx rows, so a retry re-runs the swap against a dead subscription.
  //
  // These are also the only codes besides FICA_ID_NAME_MISMATCH that may show
  // a backend sentence, and they read it off `body.message` (the envelope our
  // own handler wrote), never off `e.message` (the client's infra string).
  if (
    errorCode === 'SUBSCRIPTION_SWAP_UNRECORDED' ||
    errorCode === 'SUBSCRIPTION_NOT_ACTIVE' ||
    errorCode === 'SUBSCRIPTION_CHANGED'
  ) {
    const fromBackend = envelopeField(e, 'message');
    if (fromBackend) return fromBackend;
    if (errorCode === 'SUBSCRIPTION_SWAP_UNRECORDED') {
      return (
        'The plan change went through at Paystack, but we could not record it ' +
        'here. Do not try again. Reload the billing page in a minute, and if it ' +
        'does not show the new plan, contact Elreign support with the reference ' +
        'shown below.'
      );
    }
    if (errorCode === 'SUBSCRIPTION_CHANGED') {
      return (
        'The plan changed at Paystack, but somebody else edited this ' +
        'subscription at the same time. Reload the billing page to see where ' +
        'it stands.'
      );
    }
    // An outstanding payment cannot be settled anywhere in the product: the
    // billing screen's only payment action authorises a card, and nothing
    // re-charges an unpaid invoice. Naming a control that does not exist is
    // what this whole wave is about, so it names the one thing that works.
    return ARREARS_PROVIDER_STATUSES.has(envelopeField(e, 'providerStatus') ?? '')
      ? 'This subscription has an outstanding payment at Paystack, so the plan was not changed. It cannot be settled from this page: contact Elreign support to clear it, then change your plan.'
      : 'This subscription is not active at Paystack, so nothing was changed. Reload the billing page: the change you asked for may already have gone through.';
  }
  // Both of these lived on the billing screen itself until the branch above
  // replaced that local list. They are billing-only codes, so they cannot
  // change what any other screen says.
  if (errorCode === 'SUBSCRIPTIONS_NOT_CONFIGURED') {
    return 'Billing is not fully configured yet. Contact your Elreign operator to enable plan changes.';
  }
  if (errorCode === 'NO_ACTIVE_SUBSCRIPTION') {
    return 'There is no active subscription to change. Reload the billing page to see where this firm stands.';
  }
  // ELR-03 (round 11). Paystack will not start a subscription for a customer
  // with no card authorisation, so `subscription/start` refuses with a 409
  // before it asks. Without this branch that 409 falls to the generic conflict
  // copy below, "Someone else updated this just now", which describes a
  // different event and sends a firm round a refresh loop that cannot help.
  // Reads `body.message`, the sentence our own handler wrote, never the
  // client's infra string.
  if (errorCode === 'PAYMENT_METHOD_REQUIRED') {
    return (
      envelopeField(e, 'message') ??
      'Add a card before you subscribe. Use the payment method card on this page to authorise your card with Paystack, then start the subscription.'
    );
  }

  // --- Status-code mappings (language-agnostic) -----------------------------
  // A 401 whose code says the JWKS endpoint was unreachable is an
  // INFRASTRUCTURE failure, not an authentication one: the user's token was
  // never judged invalid — the server just couldn't fetch the keys to check
  // it. Telling that user "you are signed out" is false and unactionable
  // (2026-08-22 Klaprops walkthrough: five of these during instance scale-out
  // convinced the room the session state was corrupt; a retry was all it
  // needed). Say what actually happened.
  if (status === 401 && errorCode === 'jwks_unreachable_and_stale_cache_expired') {
    return 'The server had trouble verifying your session just now. You are not signed out. Try again.';
  }
  // A firm's per-client act-as token lapsed (15-min TTL). The operator's OWN
  // session is untouched, so "you are signed out" is false — and it sent the
  // 2026-08-23 Lekker Zol walkthrough looking for a login problem while their
  // whole reviewed structure sat uncommitted. Name the real thing.
  if (errorCode === 'ACT_AS_EXPIRED') {
    return 'Your access to this client timed out. Reopen the client from the Client Book, then try again.';
  }
  if (status === 401) {
    // 2026-08-25 visual sweep: a 401 on a PANEL's data fetch must not claim
    // "You are signed out" — the walkthrough screenshots show that sentence
    // inside a queue card and even floating over a hero that was
    // simultaneously rendering the user's live portfolio. A single endpoint
    // can 401 while the session is perfectly alive (a stale per-call token, a
    // gateway hiccup — the Klaprops meeting lost half an hour to this exact
    // wording). Panels read data ('load' context); only a whole-surface auth
    // failure may assert sign-out, and the layouts already handle that by
    // redirecting. The 'load' copy names what is actually known: this panel
    // could not verify access, retry — and only suggests re-auth as the
    // persistence branch.
    if (context === 'load') {
      return 'Couldn’t verify your access for this panel just now. Retry, and if it keeps happening, sign in again.';
    }
    // The default lands here. A caller that named no action has told us nothing
    // about what it was doing, so we know nothing about the session either:
    // say only what one 401 proves. See SESSION_VERDICT_CONTEXTS above.
    if (!SESSION_VERDICT_CONTEXTS.has(context)) {
      return 'We couldn’t verify your access just now. You may still be signed in. Try again, and if it keeps happening, sign in again.';
    }
    return 'You are signed out. Sign in again, then retry.';
  }
  if (status === 403) {
    return 'You do not have permission to do that.';
  }
  if (status === 404) {
    return 'That item is no longer available.';
  }
  if (status === 409) {
    return 'Someone else updated this just now. Refresh and try again.';
  }
  if (status === 413) {
    return 'That file is too large. Choose a smaller file and try again.';
  }
  if (status === 429) {
    return 'Too many requests in a short period. Wait a minute, then try again.';
  }
  if (typeof status === 'number' && status >= 500) {
    // Our own handlers write plain refusals on a 5xx as well. Show that
    // sentence when the body carries one that reads like a sentence; see
    // `serverSentence` for what disqualifies it and why the default is strict.
    return serverSentence(e) ?? 'The server had a problem. Please try again shortly.';
  }

  // --- Library-string fallbacks ---------------------------------------------
  if (msg.includes('network') || msg.includes('fetch failed') || msg.includes('failed to fetch')) {
    return 'Could not reach the server. Check your connection and try again.';
  }
  if (msg.includes('abort') || msg.includes('timeout') || msg.includes('timed out')) {
    return 'The request took too long. Check your connection and try again.';
  }

  // --- Context-specific catch-all -------------------------------------------
  return CONTEXT_FALLBACK[context];
}
